import {
  Audience,
  ConfigurationId,
  CUSIP,
  FundShareClassId,
  GeneralConfig,
  LiteratureData,
  LiteratureDocumentData,
  PerformanceConfig,
  PerformanceViewOption,
  ProductData,
  ProductType,
  SwitchState,
  TAID,
} from '@types';

import { BasePpssService } from './base-ppss.service';
import { FundFilterService } from './fund-filter.service';
import { FundListing } from '@models';
import { INITIAL_STATE } from './gw.ppss.config';
import { Injectable } from '@angular/core';
import { Logger } from '@utils/logger';
import { PPSSFundDataService } from '@products/services/ppss-fund-data.service';
import { SiteConfigService } from '@services/site-config.service';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { take } from 'rxjs/operators';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { AppStateService } from '@services/app-state.service';
import { combineLatest, forkJoin, Observable, Subject } from 'rxjs';
import { SegmentService } from '@services/segment.service';
import { DebugService } from '@services/debug.service';
import cloneDeep from 'lodash/cloneDeep';
import { FavoritesService } from '@products/services/favorites.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import {
  ProductDetail,
  ProductDetailService,
} from '@products/product-detail-layout/product-detail.service';
import { removeLeadingZero } from '@utils/text/string-utils';
import { Document } from '@models';
import capitalize from 'lodash/capitalize';
import { ProfileService } from '@services/profile.service';
import { RouteHistoryService } from '@services/route-history.service';

const logger = Logger.getLogger('PpssService');

export interface AccessibiltityData {
  colID: string;
  keyboardEvent: {
    code: string;
  };
}
const SettingNavGroupColumnsVisibilityEnabledCountries = new Set(['CA']);
@Injectable()
export class PpssService extends BasePpssService {
  accessibility$: Subject<AccessibiltityData>;
  isExactMatch: boolean;
  productDataBeforeFavoritesOnly: ProductData[] = [];

  constructor(
    productType: ConfigurationId,
    private fundDataService: PPSSFundDataService,
    filterService: FundFilterService,
    protected siteConfigService: SiteConfigService,
    protected caveatService: CaveatService,
    protected literatureDataService: LiteratureDataService,
    protected appStateService: AppStateService,
    protected segmentService: SegmentService,
    protected debugService: DebugService,
    protected favoritesService: FavoritesService,
    protected literatureConfigService: LiteratureGlobalConfigService,
    protected productDetailService: ProductDetailService,
    protected profileService: ProfileService,
    protected routeHistoryService: RouteHistoryService
  ) {
    super(
      productType,
      cloneDeep(INITIAL_STATE),
      filterService,
      siteConfigService,
      segmentService,
      debugService,
      productDetailService
    );
    this.accessibility$ = new Subject<AccessibiltityData>();
    filterService.getData().subscribe(([dataType, data]) => {
      if (dataType === 'filteredList') {
        this.filterProducts(data);
      }
    });
  }

  updateNumberOfRows(numberOfRows: number) {
    this.pagedFunds.pageSize = numberOfRows;
  }

  getPageSize(): number {
    return this.pagedFunds.pageSize;
  }

  getState() {
    return this.state;
  }
  setAccessibility(colID, keyboardEvent) {
    this.accessibility$.next({
      colID,
      keyboardEvent,
    });
  }
  getAccessibility$(): Observable<AccessibiltityData> {
    return this.accessibility$.asObservable();
  }
  /**
   * Initial method called by ppss component.
   * Initialises service and fetches feed.
   */
  populate(): void {
    if (this.state.hasLoadedFullFeed) {
      // already populated
      this.onPopulated();
    } else {
      this.initialiseState();
      const queries = this.fetchQueries();
      this.fundDataService
        .fetchData(queries, {
          configurationName: this.getProductType(),
          productType: this.getProductTypeParameter(this.getProductType()),
        })
        .pipe(take(1))
        .subscribe((fundListing) => {
          this.ppssStateAction(fundListing);
          this.loadFavourites();
          this.onPopulated();
          this.loadDocuments();
          this.caveatService
            .getPpssAlerts$()
            .pipe(take(1))
            .subscribe((alerts) => this.addAlerts(alerts));
        });
    }
  }

  private onPopulated() {
    // Set 'productData' for pagination as well as to filter service for future filter event(s).
    // But, take care of the same, keeping "fav only switch" in scope.
    let productsData: ProductData[] = [];

    if (this.state.config.showFavoritesOnlySwitch) {
      productsData = this.state.data.tableRows;
    } else {
      if (this.productDataBeforeFavoritesOnly.length) {
        productsData = this.productDataBeforeFavoritesOnly;
      } else {
        productsData = this.state.data.tableRows; // Contigency, just in case, fav service takes enough time or no data returned.
      }
    }

    this.pagedFunds.records = productsData;
    this.filterService.init(productsData);
    this.sendNext('populate');
  }

  updateCurrentTab(currentTab) {
    this.state.config.currentTable = currentTab;
    this.sendNext('updateRows');
  }

  /**
   * Fund data handler.
   * Maps the respective values to the needful properties.
   * @param fundListing ppss mf feed received from fund data service.
   */
  ppssStateAction(fundListing: FundListing) {
    const productsData: ProductData[] = this.getProductsData(
      fundListing.products
    );
    this.state.data.tableRows = productsData;
    this.state.data.filteredTableRows = productsData;
    this.state.data.asOfDates.navAsOfDate = fundListing.navAsOfDate;
    this.state.data.asOfDates.performanceAsOfDateMonthly =
      fundListing.performanceAsOfDateMonthly;
    this.state.data.asOfDates.performanceAsOfDateMonthlyStd =
      fundListing.performanceAsOfDateMonthlyStd;
    this.state.data.asOfDates.performanceAsOfDateQuarterly =
      fundListing.performanceAsOfDateQuarterly;
    this.state.data.asOfDates.performanceAsOfDateQuarterlyStd =
      fundListing.performanceAsOfDateQuarterlyStd;
    this.state.data.asOfDates.stdYieldThirtyDayAsofDate =
      fundListing.stdYieldThirtyDayAsofDate;
    this.state.data.asOfDates.ratingsAsOfDate = fundListing.ratingsAsOfDate;

    this.state.config.performanceStatusCaveat =
      fundListing.performanceStatus === 'FINAL'
        ? 'PPSSPerformanceFinalTop'
        : fundListing.performanceStatus === 'PRELIM'
        ? 'PPSSPerformancePreEliminaryTop'
        : null;

    if (this.siteConfigService.isNjBest()) {
      this.loadAdditionalData();
    } else {
      this.state.hasLoadedFullFeed = true;
    }
  }

  private initialiseState() {
    const generalConfig: GeneralConfig = this.siteConfigService.product
      ?.general;
    const performanceConfig: PerformanceConfig = this.siteConfigService.product
      ?.performance;

    // Extract and save performance view options.
    const performanceViewOptions: PerformanceViewOption[] = this.createViewOptions(
      performanceConfig
    );
    this.state.config.performanceViewOptions = performanceViewOptions;

    // Extract and save default performance view.
    const defaultPerformanceView: PerformanceViewOption = performanceViewOptions.find(
      (view) => view.isDefault
    );
    this.state.config.performanceView = defaultPerformanceView;

    // Extract and save period ends.
    const periodEnd: string = defaultPerformanceView?.periodEnds[0];
    this.state.config.periodEnd = periodEnd;

    // NGC-13646: SA performance currency calculation.
    if (this.siteConfigService.getPdsCountry() === 'ZA') {
      this.state.config.showPerformanceCurrency = true;

      this.siteConfigService.getCurrencyListByPerformance().subscribe(
        (currencyList) => {
          this.state.config.performanceCurrencyOptions = currencyList;
          // Here, reference is getting updated (against 'currencyListByPerformance' in site config) with fresh values.
          // Now, no need to 'sendNext'.
        },
        (error) => {
          logger.error(`Error in fetching currency list: ${error}`);
        },
        () => {
          // Intentionally added at first place to have default calculation as BAU.
          this.state.config.performanceCurrencyOptions.splice(
            0,
            0,
            'SHARE_CLASS'
          );
          this.state.config.selectedPerformanceCurrencyOption = this.state.config.performanceCurrencyOptions[0];
          // NGC-14200 : SOUTH AFRICA Products - Money Funds not displayed when selected from Select Product Type drop down
          // we need this functionality as constructor doesn't reinitialize performance currency list when new state is loaded because
          // it is being marked complete(). Hence doing this to avoid the problem and get fresh values of performance currency list
          // each time new state is being added.
          this.siteConfigService.reInitializeCurrencyListByPerformance();
          this.siteConfigService.reInitializeCurrencyListByPerformance$();
        }
      );
    }

    // Save site configurations.
    this.state.isSiteIntl = this.isSiteIntl;
    this.state.siteConfig = this.siteConfigService.product?.site;

    // PPSS tabs visibility.
    this.state.config.tabs.showRatings = this.siteConfigService.showRatings(
      this.getProductType() as ProductType
    );
    this.state.config.tabs.showYield = this.siteConfigService.showYields(
      this.getProductType() as ProductType
    );
    this.state.config.tabs.showFundIdentifiers = this.siteConfigService.showIdentifiers(
      this.getProductType() as ProductType
    );
    // NGC-15833: Show Price PPSS for NJbest from state, as PPSS filter search and
    // changing tabs causes router navigation for the first time and doesn't change tab
    this.state.config.tabs.showPrice = this.siteConfigService.isNjBest();

    // PPSS other visibility conditions.
    this.state.config.hideNavChange = this.siteConfigService.hideNavChange(
      this.getProductType() as ProductType
    );
    this.state.config.hideFactsheet = this.siteConfigService.hideFactsheet(
      this.getProductType() as ProductType
    );

    // NGC-1396

    this.state.config.identifierList = this.siteConfigService.getFundIdentifiers(
      this.getProductType() as ProductType
    );
    // NGC-1351.
    // Suppress performance details for intl, if present.
    if (generalConfig.showMDashForUnder1Year) {
      this.state.config.suppressDetails = {
        showMDashForUnder1Year: generalConfig.showMDashForUnder1Year,
      };
    }
    if (generalConfig.showMDashForUnder6Months) {
      this.state.config.suppressDetails = {
        showMDashForUnder6Months: generalConfig.showMDashForUnder6Months,
      };
    }

    this.state.config.calcType = this.siteConfigService.getDefaultCalcType(
      this.getProductType() as ProductType
    );

    // WDE-1260: Initialize favorites only state.
    // Check if user is logged in along with a truthy flag from BR config.
    if (
      this.profileService.isLoggedIn() &&
      this.siteConfigService.showFavoritesOnly(
        this.getProductType() as ProductType
      )
    ) {
      this.state.config.showFavoritesOnlySwitch = true;
    } else {
      this.state.config.showFavoritesOnlySwitch = false;
    }

    if (this.state.config.showFavoritesOnlySwitch) {
      // ********** LOGIC **********
      // Fetch the current URL from route history, which will be its last record.
      // Now, if the current URL consist of fragment 'favoriteProductWidget', it means user navigated from "Favorite fund widget".
      // Also, check the condition for possibility of direct page load, i.e., route history must not have length as '1'.
      const routeHistory: string[] = this.routeHistoryService.getRouteHistory();
      const currentURL: string = routeHistory[routeHistory.length - 1];
      const isNavigationFromFavProdWidget: boolean =
        currentURL?.includes('favoriteProductWidget') &&
        routeHistory.length > 1;

      // Further, if the current listing page is navigated through "Favorite fund widget" on home page.
      // Then, show the fav only toggle as 'on'.
      // Else, every other case, from any other navigation source, show state as 'off'.
      // This will cover business criteria as fav only toggle being 'off', when -
      // 1. User navigates from home page through global navigation such as investment options.
      // 2. User directly loads up the URL through direct paste or URL being bookmarked, while user is signed in.
      if (isNavigationFromFavProdWidget) {
        this.state.config.favoriteOnlySwitchState = SwitchState.ON;
      } else {
        this.state.config.favoriteOnlySwitchState = SwitchState.OFF;
      }
    }
  }

  // NGC-14704: Load Additional fields from product details query as
  // PDS is unable to add them in 529 for NJBest
  private loadAdditionalData(): void {
    const observables: Observable<ProductDetail>[] = [];

    this.state.data.tableRows.forEach((product: ProductData) => {
      // NGC-15833: Remove Spaces from CUSIP and showing portfolio
      // number as 3 digit at the source,so that it works correctly with fund listing search
      product.shareClass.identifiers.cusip = product?.shareClass?.identifiers?.cusip?.replace(
        /\s/g,
        ''
      ) as CUSIP;
      product.shareClass.identifiers.taid = removeLeadingZero(
        product?.shareClass?.identifiers?.taid
      ) as TAID;

      const obs: Observable<ProductDetail> = this.productDetailService.getProductDetails$(
        product.fundId,
        product.shareClass.shareClassCode
      );

      observables.push(obs);

      obs.subscribe((productDetail: ProductDetail) => {
        product.shareClass.nav.highestNavValueDateIn52Weeks =
          productDetail.nav.highestNavValueDateIn52Weeks;
        product.shareClass.nav.highestNavValueIn52Weeks =
          productDetail.nav.highestNavValueIn52Weeks;
        product.shareClass.nav.lowestNavValueDateIn52Weeks =
          productDetail.nav.lowestNavValueDateIn52Weeks;
        product.shareClass.nav.lowestNavValueIn52Weeks =
          productDetail.nav.lowestNavValueIn52Weeks;
        product.shareClass.charges.annualCharge =
          productDetail.charges.annualCharge;
      });
    });

    forkJoin(observables).subscribe(() => {
      this.state.hasLoadedFullFeed = true;
      this.sendNext('populate');
      // fix for not updated filtered records number on init
      this.sendNext('filterRows');
    });
  }

  private loadFavourites(): void {
    this.favoritesService.getFavoritesOnLoad$().subscribe((favorites) => {
      if (favorites.length > 0) {
        // Set favorite boolean flag as true for funds accordingly.
        this.state.data.tableRows.forEach((product: ProductData, index) => {
          product.isFavoriteFund = false;
          if (
            favorites.includes(
              `${product.fundId}-${product?.shareClass?.identifiers?.shareClassCode}` as FundShareClassId
            )
          ) {
            product.isFavoriteFund = true;
          }
        });

        // Then, sort the listing with favorite fund as being on top.
        this.state.data.tableRows = this.sortByFavFund(
          this.state.data.tableRows
        );
      }

      // Also, save the current listing for future reference.
      this.productDataBeforeFavoritesOnly = cloneDeep(
        this.state.data.tableRows
      );

      // Further, return favorite only listing when switch button is on.
      this.state.data.tableRows = this.processFavoritesOnlyListing();

      // Finally, update the grid.
      this.sendNext('populate');
    });
  }

  /**
   * Toggle favorite only listing as per updated state received.
   * @param updatedSwitchState new switch state when button switched
   */
  toggleFavoritesOnlyListing(updatedSwitchState: SwitchState): void {
    this.state.config.favoriteOnlySwitchState = updatedSwitchState;

    // Fetch listing as per switch state.
    const favOnlyProductsData: ProductData[] = this.processFavoritesOnlyListing();

    // Save the new fav only listing in grid variables.
    this.state.data.tableRows = favOnlyProductsData;
    this.state.data.filteredTableRows = favOnlyProductsData;
    this.pagedFunds.records = favOnlyProductsData;

    // Also, save the listing to filter service for future filter event(s).
    this.filterService.setProducts(favOnlyProductsData);

    // Finally, Update the grid.
    this.sendNext('populate');
  }

  /**
   * Return favorite only listing when its corresponding switch button is on.
   */
  private processFavoritesOnlyListing(): ProductData[] {
    return this.state.config.showFavoritesOnlySwitch &&
      this.state.config.favoriteOnlySwitchState === SwitchState.ON
      ? this.productDataBeforeFavoritesOnly.filter(
          (tableRow) => tableRow.isFavoriteFund
        )
      : this.productDataBeforeFavoritesOnly;
  }

  private loadDocuments(): void {
    if (this.getProductType() !== ConfigurationId._529) {
      const litParamsFactsheetAndProfileDocs = new Map();
      litParamsFactsheetAndProfileDocs.set(
        'type',
        'FACT-SHEET,PRODUCT-PROFILE'
      );
      litParamsFactsheetAndProfileDocs.set('cache', 'true');
      const loadFactsheetAndProfileDocs$ = this.literatureDataService.loadData(
        litParamsFactsheetAndProfileDocs,
        false,
        true
      );

      loadFactsheetAndProfileDocs$.pipe(take(1)).subscribe((docs) => {
        this.addDocuments(docs);
      });
    }
  }

  updateFavourite(fund: FundShareClassId): void {
    const selctedIndex = this.state.data.tableRows.findIndex(
      (row) =>
        `${row.fundId}-${row.shareClass.identifiers.shareClassCode}` === fund
    );
    if (this.state.data.tableRows[selctedIndex]) {
      this.state.data.tableRows[selctedIndex].isFavoriteFund = !this.state.data
        .tableRows[selctedIndex]?.isFavoriteFund;
    }

    this.sendNext('updateRows');
  }

  addAlerts(alerts): void {
    if (alerts) {
      this.state.data.tableRows.forEach((row) => {
        row.ppssAlert =
          alerts[`${row.fundId}-${row.shareClass.shareClassCode}`] ||
          alerts[row.fundId];
      });
      this.sendNext('populate'); // TODO can we just refresh one column?
    }
  }

  // we dont have documents for each share class so need to map at fund level as well.
  /* As per the latest literature Api response we only have two records for each fund-
   * one is with frkShareClassCode Z or Adviser, will be mapped for Adviser share class
   * and the second record will be mapped for all other shareclasses
   */

  /*
    TODO: this method needs cleaned up:
    - typing can be improved
    - inappropriate use of Array.find()
    - too many levels of nested logic makes it complicated to read
    - too much in a single function - should be split into several private functions
  */
  addDocuments(docs: LiteratureData): void {
    // to map fund it with primary share class code
    const primaryShareClassMap = new Map<string, string>();
    this.state.data.tableRows.forEach((row) => {
      if (!primaryShareClassMap.get(row.fundId)) {
        if (row.shareClass.primaryShareClass) {
          primaryShareClassMap.set(row.fundId, row.shareClass.shareClassCode);
        }
      }
    });
    // first create map of documents keyed by fundId and shareClassCode
    this.state.data.tableRows.forEach((row) => {
      let primaryShareClassMapLiteratureData: LiteratureDocumentData;
      let secondaryShareClassMapLiteratureData: LiteratureDocumentData;
      let docTermsType;
      docTermsType =
        this.getProductType() === ConfigurationId.TIF
          ? 'PRODUCT-PROFILE'
          : 'FACT-SHEET';
      let fundAllDocs = docs?.document.filter((doc) => {
        return (
          doc.frkOneTISNumber === row.fundId && doc.dctermsType === 'FACT-SHEET'
        );
      });
      if (
        ((this.getProductType() === ConfigurationId.SMA ||
          row?.investmentManager?.toLowerCase().includes('franklin')) &&
          fundAllDocs?.length === 0) ||
        this.getProductType() === ConfigurationId.TIF
      ) {
        fundAllDocs = docs?.document.filter((doc) => {
          return (
            doc.frkOneTISNumber === row.fundId &&
            doc.dctermsType === 'PRODUCT-PROFILE'
          );
        });
      }
      // to store primary share class mapped data
      fundAllDocs.forEach((litData) => {
        if (primaryShareClassMap.get(litData.frkOneTISNumber)) {
          const shareClassArray = litData.frkShareClassCode
            ? litData.frkShareClassCode.split(',').map((item) => item.trim())
            : [];
          if (
            shareClassArray.includes(
              primaryShareClassMap.get(litData.frkOneTISNumber)
            )
          ) {
            primaryShareClassMapLiteratureData = cloneDeep(litData);
          } else if (
            (shareClassArray.includes('Z') ||
              shareClassArray.includes('Advisor')) &&
            (primaryShareClassMap.get(litData.frkOneTISNumber) === 'Z' ||
              primaryShareClassMap.get(litData.frkOneTISNumber) === 'Advisor')
          ) {
            primaryShareClassMapLiteratureData = cloneDeep(litData);
          } else {
            secondaryShareClassMapLiteratureData = cloneDeep(litData);
          }
        }
      });
      let primaryDoc: LiteratureDocumentData;
      let match = false;
      if (row.shareClass.shareClassCode === 'Z') {
        primaryDoc = fundAllDocs.find((data) => {
          if (data?.frkShareClassCode) {
            if (
              data?.frkShareClassCode.match('Z|Advisor') &&
              this.checkAudience(
                data?.dctermsAudience,
                this.literatureConfigService.literatureConfig
                  ?.fundDocumentSegmentOverrideList
              )
            ) {
              match = true;
              return data;
            } else {
              match = false;
            }
          }
        });
        if (
          !match &&
          this.literatureConfigService
            .getDocumentMappingConfiguration()
            .get('FACT-SHEET') === 'FUND_ID' &&
          this.checkAudience(
            secondaryShareClassMapLiteratureData?.dctermsAudience,
            this.literatureConfigService.literatureConfig
              ?.fundDocumentSegmentOverrideList
          )
        ) {
          primaryDoc = secondaryShareClassMapLiteratureData;
        }
      } else {
        this.isExactMatch = false;
        primaryDoc = fundAllDocs.find((data) => {
          if (
            data?.frkShareClassCode &&
            this.checkAudience(
              data?.dctermsAudience,
              this.literatureConfigService.literatureConfig
                ?.fundDocumentSegmentOverrideList
            )
          ) {
            return this.checkShareClass(data, row.shareClass.shareClassCode);
          } else {
            this.isExactMatch = false;
          }
        });
        if (
          !this.isExactMatch &&
          this.literatureConfigService
            .getDocumentMappingConfiguration()
            .get('FACT-SHEET') === 'FUND_ID' &&
          (this.checkAudience(
            secondaryShareClassMapLiteratureData?.dctermsAudience,
            this.literatureConfigService.literatureConfig
              ?.fundDocumentSegmentOverrideList
          ) ||
            this.checkAudience(
              primaryShareClassMapLiteratureData?.dctermsAudience,
              this.literatureConfigService.literatureConfig
                ?.fundDocumentSegmentOverrideList
            ))
        ) {
          primaryDoc =
            secondaryShareClassMapLiteratureData ||
            primaryShareClassMapLiteratureData;
        }
      }
      row.documents = this.setRowDocument(primaryDoc);
    });
    this.sendNext('populate'); // TODO can we just refresh one column?
  }

  checkAudience(
    LiteratureAudiences: string[],
    segmentOverrideList: string[]
  ): boolean {
    let audienceFound = false;
    const audienceEnum = LiteratureAudiences?.map((key) => capitalize(key));
    const audiences = LiteratureAudiences?.map((key) => Audience[key]);
    if (segmentOverrideList?.length > 0) {
      for (const segment of segmentOverrideList) {
        if (audienceEnum?.includes(capitalize(segment))) {
          audienceFound = true;
        } else if (audiences?.includes(capitalize(segment))) {
          audienceFound = true;
        }
        if (audienceFound) {
          break;
        }
      }
    } else {
      if (
        audienceEnum?.includes(
          capitalize(this.segmentService.getCurrentSegmentId())
        )
      ) {
        audienceFound = true;
      } else if (
        audiences?.includes(this.segmentService.getCurrentSegmentId())
      ) {
        audienceFound = true;
      }
    }
    return audienceFound;
  }

  private setRowDocument(primaryDoc: LiteratureDocumentData): Document[] {
    if (this.isSiteIntl && primaryDoc?.literatureHref) {
      return this.getDocuments(primaryDoc, this.isSiteIntl);
    } else if (primaryDoc?.customFilePath) {
      return this.getDocuments(primaryDoc, this.isSiteIntl);
    }
  }

  private getDocuments(
    primaryDoc: LiteratureDocumentData,
    isSiteIntl: boolean
  ): Document[] {
    const document = {
      type: primaryDoc.dctermsType,
      link: isSiteIntl
        ? `${this.appStateService.getLiteratureDownloadBaseUrl()}${
            primaryDoc.literatureHref
          }`
        : `${this.appStateService.getLiteratureDownloadUrl()}${
            primaryDoc.customFilePath
          }`,
      showOnPpss: true,
      downloadUrl: primaryDoc.downloadUrl,
    };
    return [document];
  }

  checkShareClass(
    data: LiteratureDocumentData,
    pdsShareClassCode: string
  ): LiteratureDocumentData {
    let shareClassArray = data.frkShareClassCode
      .split(',')
      .map((item) => item.trim());
    shareClassArray = this.mapRomanAndNumericShareClass(shareClassArray);
    if (shareClassArray.includes(pdsShareClassCode)) {
      this.isExactMatch = true;
      return data;
    }
    this.isExactMatch = false;
  }

  mapRomanAndNumericShareClass(shareClassArray: string[]): string[] {
    if (shareClassArray.includes('1') && !shareClassArray.includes('I')) {
      shareClassArray.push('I');
    }
    if (shareClassArray.includes('2') && !shareClassArray.includes('II')) {
      shareClassArray.push('II');
    }
    if (shareClassArray.includes('I') && !shareClassArray.includes('1')) {
      shareClassArray.push('1');
    }
    if (shareClassArray.includes('II') && !shareClassArray.includes('2')) {
      shareClassArray.push('2');
    }
    return shareClassArray;
  }

  isSettingNavGroupColumnsVisibilityEnabled(): boolean {
    return (
      !this.siteConfigService.isSiteInternational() ||
      SettingNavGroupColumnsVisibilityEnabledCountries.has(
        this.siteConfigService.getPdsCountry()
      )
    );
  }
}
